import { createStyles } from '@mantine/core';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useLayoutEffect, useRef, type PropsWithChildren } from 'react';

import { titleStyles } from '~/domains/design-system/Title';

export const GAMESHELL_SUBHEADER_HEIGHT = 48;

const useStyles = createStyles((theme) => ({
  nav: {
    listStyle: 'none',
    display: 'flex',
    minWidth: 'max-content',
  },

  navItemLink: {
    minWidth: 'max-content',
    height: GAMESHELL_SUBHEADER_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    color: theme.white,
    textDecoration: 'none',
    ...titleStyles['headline-small'],
    padding: `0 ${theme.spacing.sm}px`,
    borderBottom: '2px solid transparent',
    transition: 'border-bottom-color 0.1s ease-in-out',

    '&.isActive': {
      color: theme.colors.blue[3],
      borderBottomColor: theme.colors.blue[3],
    },

    '@media (hover: hover)': {
      transition: 'color 0.1s ease-in-out, border-bottom-color 0.1s ease-in-out',

      '&:hover': {
        color: theme.colors.splash[5],
      },
    },
  },
}));

export function GamecenterScheduledNavList({ children }: PropsWithChildren) {
  const { classes } = useStyles();

  return <nav className={classes.nav}>{children}</nav>;
}

export function GamecenterScheduledNavListItem({
  children,
  href,
  activeForRoutes = [],
  ...rest
}: PropsWithChildren<{ href: string; activeForRoutes?: string[]; 'data-test-id'?: string }>) {
  const { classes, cx } = useStyles();
  const { asPath } = useRouter();
  const elRef = useRef<HTMLLIElement>(null);

  const isActive = [href, ...activeForRoutes].some((h) => asPath.startsWith(h));

  useLayoutEffect(() => {
    if (isActive) {
      elRef.current?.scrollIntoView({
        block: 'center',
        inline: 'center',
      });
    }
  }, [isActive]);

  return (
    <li ref={elRef}>
      <Link
        href={href}
        data-test-id={rest['data-test-id']}
        className={cx(classes.navItemLink, { isActive })}
      >
        {children}
      </Link>
    </li>
  );
}
